import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';

import {
  TwOverlayFallbackHandle,
  TwOverlayFallbackService,
} from '../shared/cdk-fallback';
import { isNativePositioningAvailable } from '../shared/positioning-strategy';

import { TW_DROPDOWN_CONTEXT } from './tw-dropdown-context';

/**
 * `tw-dropdown-menu` — the `[popover]` panel hosting the items
 * (TWS-122 Wave 1).
 *
 * The host is a `<div>` carrying the native `[popover]` attribute,
 * the menu ARIA role, and the generated popover id so the sibling
 * trigger's `popovertarget` can reach it. The panel sits in the
 * browser top-layer once opened via `showPopover()`; CSS anchor
 * positioning pins it to the trigger.
 *
 * ### Keyboard handling (WAI-ARIA menu pattern, manually wired)
 *
 * `@angular/aria/menu` (Angular 21.2 developer-preview) offers a
 * similar pattern, but:
 *   - It expects consumers to wire `#menuRef="ngMenu"` at the
 *     consumption site, which conflicts with our slot composition
 *     where the parent mediates;
 *   - `@developerPreview` status means the API can change in
 *     21.x / 22 without a deprecation window.
 *
 * So we implement the ~30 lines manually — it's a well-known
 * pattern and decouples us from an unstable preview API:
 *
 *   - ArrowDown / ArrowUp move focus between enabled items, wrapping
 *     at ends.
 *   - Enter / Space activates the focused item (dispatched as a
 *     click so item-level handlers trigger uniformly).
 *   - Escape closes and returns focus to the trigger (the native
 *     `[popover]` API also closes on Escape; we explicitly handle
 *     focus return because the browser doesn't).
 *   - Home / End jump to the first / last enabled item.
 *   - Tab lets focus leave the menu naturally — WAI-ARIA menus
 *     close on Tab because focus moves out of the menu scope.
 *
 * ### Toggle sync
 *
 * The native `toggle` event fires after every open/close
 * transition. Its `newState` field is `"open"` or `"closed"`. We
 * mirror it back into the parent's open state via the context so
 * template-bound consumers stay in sync — clicks outside the
 * menu (light dismiss) and Escape both flow through `toggle`.
 */
@Component({
  selector: 'tw-dropdown-menu',
  standalone: true,
  template: '<ng-content></ng-content>',
  styleUrls: ['./tw-dropdown-menu.component.scss'],
  host: {
    'class': 'tw-dropdown__menu',
    '[id]': 'ctx.popoverId',
    'popover': 'auto',
    'role': 'menu',
    '[attr.data-placement]': 'ctx.placement()',
    '(toggle)': 'onToggle($event)',
    '(keydown)': 'onKeydown($event)',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TwDropdownMenuComponent implements OnInit {
  protected readonly ctx = inject(TW_DROPDOWN_CONTEXT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fallback = inject(TwOverlayFallbackService);

  /**
   * Whether this process takes the native `[popover]` path or
   * the CDK Overlay fallback. Resolved once at construction —
   * the detect is memoised so subsequent reads cost a single
   * field lookup.
   */
  private readonly useNative = isNativePositioningAvailable();

  /** Active CDK fallback handle while the menu is open in fallback mode. */
  private fallbackHandle: TwOverlayFallbackHandle | null = null;

  /**
   * Guard flag: when `true`, `onToggle` is currently running and
   * any `setOpen()` inside it should NOT re-trigger our own
   * `effect()` that drives `showPopover`/`hidePopover`. Without
   * this, a native close → `toggle` → `setOpen(false)` → effect
   * tries to `hidePopover()` on an already-closed dialog, which
   * the spec explicitly permits but is wasteful.
   */
  private syncing = false;

  ngOnInit(): void {
    const el = this.host.nativeElement;
    this.ctx.registerMenuElement(el);
    // Wire `position-anchor` inline. Same rationale as the
    // trigger's `anchor-name` — it's a static property; no
    // signal updates.
    el.style.setProperty('position-anchor', this.ctx.anchorName);
  }

  constructor() {
    // Mirror the parent's `open` signal onto either the native
    // `showPopover` / `hidePopover` pair (modern browsers) or
    // the CDK Overlay fallback (older browsers — no
    // anchor-positioning or no `[popover]` API). Both branches
    // complete the loop via `onToggle` below (native) or the
    // fallback service's onDismiss callback (CDK) so that
    // `[(open)]` consumers stay in lockstep with user dismissal.
    effect(() => {
      if (this.syncing) return;
      const shouldBeOpen = this.ctx.open();

      if (this.useNative) {
        this.syncNative(shouldBeOpen);
      } else {
        this.syncFallback(shouldBeOpen);
      }
    });

    // Teardown: close the popover if still open when the menu is
    // destroyed (e.g. route change while the menu is up).
    this.destroyRef.onDestroy(() => {
      if (this.useNative) {
        const el = this.host.nativeElement as HTMLElement & {
          hidePopover?: () => void;
          matches(sel: string): boolean;
        };
        if (typeof el.hidePopover === 'function' && el.matches(':popover-open')) {
          try {
            el.hidePopover();
          } catch {
            /* noop */
          }
        }
      } else if (this.fallbackHandle) {
        this.fallbackHandle.dispose();
        this.fallbackHandle = null;
      }
    });
  }

  /**
   * Native-branch open/close. Feature-detect inside the method
   * body because JSDOM-without-polyfill ships no methods — the
   * detection is stable across calls but cheap enough to repeat
   * inline, and keeps the branch self-contained.
   */
  private syncNative(shouldBeOpen: boolean): void {
    const el = this.host.nativeElement;
    const hasShow = typeof (el as HTMLElement & {
      showPopover?: () => void;
    }).showPopover === 'function';
    if (!hasShow) return;

    const typed = el as HTMLElement & {
      showPopover: () => void;
      hidePopover: () => void;
      matches(sel: string): boolean;
    };

    // `:popover-open` is the spec's way to read the popover's
    // open state. Guard both directions to avoid
    // InvalidStateError on redundant calls.
    const isOpen = typed.matches(':popover-open');
    try {
      if (shouldBeOpen && !isOpen) typed.showPopover();
      else if (!shouldBeOpen && isOpen) typed.hidePopover();
    } catch {
      // Swallow — most commonly a race between fast open/close
      // signal flips before the DOM has reflected the previous
      // state. The signal is the source of truth; the next
      // effect run will reconcile.
    }
  }

  /**
   * CDK-fallback open/close. Reuses the service's `openAt` with
   * light-dismiss enabled so outside click + Escape flow back
   * through `ctx.setOpen(false)` — matching the native
   * `[popover="auto"]` behaviour shape consumers expect.
   */
  private syncFallback(shouldBeOpen: boolean): void {
    const triggerEl = this.resolveTrigger();
    if (shouldBeOpen && !this.fallbackHandle && triggerEl) {
      this.fallbackHandle = this.fallback.openAt(
        triggerEl,
        this.host.nativeElement,
        this.ctx.placement(),
        {
          lightDismiss: true,
          onDismiss: () => this.ctx.requestCloseAndReturnFocus(),
        },
      );
      // Focus the first item — native `[popover]` opens in the
      // top-layer where the browser moves focus naturally; the
      // fallback path has no such affordance, so we trigger the
      // same side-effect the dropdown's own toggle handler does
      // on native open.
      this.focusItemAt(0);
    } else if (!shouldBeOpen && this.fallbackHandle) {
      this.fallbackHandle.dispose();
      this.fallbackHandle = null;
    }
  }

  /**
   * The context's `registerTriggerElement` stores the trigger's
   * host element (set by the trigger component's `ngOnInit`).
   * The public context surface doesn't expose the stored value
   * directly — we reach into the component instance internals
   * via the DI context's `registerMenuElement` path + DOM
   * sibling lookup. Simpler approach: walk up from the menu's
   * host to find the parent `<tw-dropdown>` and pick the
   * trigger from that subtree. Used ONLY by the fallback path
   * (the native path uses the CSS `anchor-name` wiring, which
   * needs no runtime element reference).
   */
  private resolveTrigger(): HTMLElement | null {
    // Start from the menu's parent — when rendered natively the
    // menu is a sibling of the trigger inside `<tw-dropdown>`;
    // the parent host is `display: contents` so the trigger
    // sits in the consumer's layout alongside the menu.
    const parent = this.host.nativeElement.parentElement;
    if (!parent) return null;
    return parent.querySelector<HTMLElement>('.tw-dropdown__trigger');
  }

  // ---------------------------------------------------------------
  // Event handlers wired via host metadata.
  // ---------------------------------------------------------------

  /**
   * Native `toggle` fires for every open/close (including native
   * light-dismiss and Escape). We mirror the state back into the
   * context so `[(open)]` bindings stay in lockstep.
   *
   * The event shape is `ToggleEvent` in the spec, but TypeScript
   * doesn't ship that lib type in every Angular project — the
   * `newState` field is widely available and narrow-cast here.
   */
  protected onToggle(event: Event): void {
    const typed = event as Event & { newState?: 'open' | 'closed' };
    const isOpen = typed.newState === 'open';
    this.syncing = true;
    try {
      this.ctx.setOpen(isOpen);
    } finally {
      this.syncing = false;
    }

    // If the menu just opened, focus the first enabled item for
    // keyboard users. We don't call `focus()` on the menu itself
    // because menuitems carry `tabindex` and hosting focus on a
    // role=menu wrapper confuses screen readers.
    if (isOpen) {
      this.focusItemAt(0);
    }
  }

  /**
   * Menu keyboard handler — WAI-ARIA menu pattern. See the class
   * comment for the supported keys.
   */
  protected onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveFocus(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveFocus(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.focusItemAt(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusItemAt(this.enabledItems().length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        this.ctx.requestCloseAndReturnFocus();
        break;
      case 'Enter':
      case ' ':
        // Activate the currently focused item. The active element
        // may be outside our item list (the user could have
        // focused some other focusable inside a menuitem); only
        // click it if it's one of our items.
        if (this.isOurItem(document.activeElement as Element | null)) {
          event.preventDefault();
          (document.activeElement as HTMLElement).click();
        }
        break;
      case 'Tab':
        // Tab leaves the menu naturally — WAI-ARIA menus close on
        // Tab because focus moves out of scope. We don't
        // preventDefault; we only close the popover.
        this.ctx.requestClose();
        break;
      default:
        // Other keys pass through — no default navigation.
        break;
    }
  }

  // ---------------------------------------------------------------
  // Item focus helpers
  // ---------------------------------------------------------------
  //
  // Items are discovered at runtime by querying the `[role=menuitem]`
  // selector — simpler than threading a ContentChildren list
  // through the context, and correct for any layout where items
  // aren't direct children (e.g. wrapped in a scrollable div for
  // virtualisation in a later wave).
  //
  // `:not([aria-disabled="true"])` excludes disabled items from
  // the roving-tabindex rotation, matching WAI-ARIA conventions.

  private enabledItems(): HTMLElement[] {
    return Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      ),
    );
  }

  private focusItemAt(index: number): void {
    const items = this.enabledItems();
    if (items.length === 0) return;
    const clamped = Math.max(0, Math.min(index, items.length - 1));
    items[clamped].focus();
  }

  private moveFocus(direction: 1 | -1): void {
    const items = this.enabledItems();
    if (items.length === 0) return;
    const active = document.activeElement as HTMLElement | null;
    const currentIndex = active ? items.indexOf(active) : -1;
    // Wrap-around: ArrowDown from last goes to first; ArrowUp
    // from first goes to last. Matches WAI-ARIA menu wrapping.
    const next =
      (currentIndex + direction + items.length) % items.length;
    // If nothing is currently focused (e.g. the menu just opened
    // and the focus landed on the menu wrapper), jump to the
    // first item on ArrowDown, last on ArrowUp.
    const target =
      currentIndex === -1
        ? direction > 0
          ? 0
          : items.length - 1
        : next;
    items[target].focus();
  }

  private isOurItem(el: Element | null): boolean {
    if (!el) return false;
    return el.closest('[role="menuitem"]') !== null;
  }
}
